import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { User } from '../../types'
import VehicleLookup from '../VehicleLookup'
import { VehicleData, calculateMargin, validateMargin, checkPPSR, getVehiclePhotos, PPSRData, PhotoData, AustralianState } from '../../lib/autograb'
import './CreateDealForm.css'
import LocationAutocomplete from '../LocationAutocomplete'
import { suggestedBuyPrice, overSuggestion, targetMarginDollars, money0 } from '../../lib/marginGuide'
import { ppsrService } from '../../services/ppsrService'
import { treatmentOf } from '../../lib/gstTreatment'
import FileDropZone from '../FileDropZone'
import { autoAdvanceDealIfReady } from '../../lib/dealStatusService'
import { isValidAbn, lookupAbn, searchAbnByName, AbrResult } from '../../services/buyerService'
/* Winnie, 15 Sep: "Inflow seller details (Type 1-5) should create or match a Contact the same
   way." Done once, when the deal is created, rather than on every keystroke of the seller
   name — a half-typed name is not a contact. */
import { contactsService, ContactType } from '../../services/contactsService'
import ContactSavePrompt from '../ContactSavePrompt'
import { toast } from '../Toast'
import type { AbrNameMatch } from '../../services/buyerService'

/**
 * Trim and collapse whitespace on a make or model before it is stored.
 *
 * Casing is deliberately left alone. Autograb writes CR-V, CLA-Class and N-Series,
 * and "helpfully" re-casing those would create exactly the split this guards against.
 */
const tidyName = (raw: string): string => raw.trim().replace(/\s+/g, ' ')

interface CreateDealFormProps {
  userId: string
  user: User
  onSuccess: () => void
  /** Optional row handed over from the vehicle search / sourcing table. */
  prefill?: {
  /* The AutoGrab sourcing row this came from. Carried through so that, once the deal
     exists, the listing can be stamped as used and drop off the sourcing list. */
  sourcing_listing_id?: string
    make?: string
    model?: string
    year?: number
    vin?: string
    rego?: string
    odometer_km?: number
    price?: number
    seller_name?: string
  } | null
}

interface AdminSettings {
  margin_threshold_percent: number
  margin_threshold_dollars: number
}


/**
 * The seller type decides the due-diligence document set, the GST treatment and
 * the PPSR grantor, so it is asked before the deal form rather than buried in
 * it — Winnie's request on 24 August: pick the vehicle, then say who is selling.
 */
type SellerType = 'individual' | 'company' | 'dealer' | 'auction' | 'tender'

const SELLER_TYPES: { value: SellerType; label: string; note: string }[] = [
  { value: 'individual', label: 'Private individual', note: 'Margin scheme — notional input tax credit.' },
  { value: 'company', label: 'Private company', note: 'Tax invoice — ABN checked against the register.' },
  { value: 'dealer', label: 'LMCT / dealer', note: 'Tax invoice — licensed motor dealer.' },
  { value: 'auction', label: 'Auction', note: 'Tax invoice — auction house.' },
  { value: 'tender', label: 'Tender', note: 'Tax invoice — tender or fleet disposal.' },
]

export default function CreateDealForm({ userId, user, onSuccess, prefill }: CreateDealFormProps) {
  const [step, setStep] = useState<'lookup' | 'sellerType' | 'form'>('lookup')
  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null)
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [margin, setMargin] = useState<any>(null)
  const [ppsr, setPPSR] = useState<PPSRData | null>(null)
  const [ppsr_loading, setPpsr_loading] = useState(false)
  const [sourcingListingId, setSourcingListingId] = useState<string | null>(null)
  const [photos, setPhotos] = useState<PhotoData | null>(null)

  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    vehicle_vin: '',
    vehicle_color: '',
    vehicle_odometer: '',
    /* Winnie, 10 Sep: read these off the invoice too, rather than retyping them. */
    vehicle_rego: '',
    vehicle_rego_state: '',
    vehicle_engine_number: '',
    seller_lmct: '',
    purchase_reference: '',
    seller_name: '',
    seller_email: '',
    seller_phone: '',
    seller_location: '',
    seller_abn: '',
    valuation_amount: '',
    seller_type: 'individual' as SellerType,
    purchase_override_reason: '',
    notes: '',
  })

  // A stolen / written-off PPSR result must be consciously acknowledged before
  // the deal can be saved. Previously it was advisory text you could scroll past.
  const [ppsrAcknowledged, setPpsrAcknowledged] = useState(false)

  // Winnie, 1 Sep: checkpoint (a). Compulsory at deal creation, and three-state on
  // purpose - a deal nobody has answered for must never read as "no finance owing".
  const [payoutApplicable, setPayoutApplicable] = useState<boolean | null>(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  /*
   * Winnie's Type 3 seller flow. A company, dealer, auction house or tender
   * seller trades under an ABN, and the sales team is usually handed a trading
   * name rather than the number. Searching the register by name fills the ABN
   * in; checking the ABN then fills the legal entity name, which is the name a
   * purchase agreement has to be drawn in. Two steps, because a trading-name
   * match comes back as the trading name and not the legal one.
   */
  const [abrBusy, setAbrBusy] = useState(false)
  const [abrNote, setAbrNote] = useState<string | null>(null)
  /* The whole lookup result, not just its sentence — the Contacts prompt needs to know the
     answer came from the register rather than from the registry itself. */
  const [abrResult, setAbrResult] = useState<AbrResult | null>(null)
  const [abrSearchTerm, setAbrSearchTerm] = useState('')
  const [abrMatches, setAbrMatches] = useState<AbrNameMatch[]>([])
  const [abrSearchBusy, setAbrSearchBusy] = useState(false)

  // Winnie, 1 Sep: reading a Type 3 seller's details off the invoice they sent.
  const [invoiceBusy, setInvoiceBusy] = useState(false)
  /* Kept so the invoice can be filed against the deal once the deal exists. Reading it
     fills the form; filing it is what lets a Type 3/4/5 purchase reach Committed. */
  const [invoiceFile, setInvoiceFile] = useState<
    { dataUrl: string; filename: string; contentType: string } | null
  >(null)
  const [invoiceNote, setInvoiceNote] = useState<string | null>(null)

  /*
   * Nick, 8 Sep: the register should answer as you type, not after a button press.
   *
   * Two directions. Type eleven digits and the legal entity name arrives; type a
   * company name and the ABNs behind it come back to choose from. The manual buttons
   * stay, because someone who wants to force a re-check should be able to.
   *
   * Both are debounced and guarded, because every call is metered against our GUID:
   * the ABN lookup fires once per distinct checksum-valid ABN and never repeats it,
   * and the name search waits for a pause in typing and never runs once an ABN is
   * known. A form that quietly spends a paid lookup on every keystroke is a form
   * somebody eventually turns off.
   */
  const abnAlreadyChecked = useRef<string>('')

  useEffect(() => {
    const digits = String(formData.seller_abn || '').replace(/\D/g, '')
    if (digits.length !== 11 || !isValidAbn(digits)) return
    if (digits === abnAlreadyChecked.current) return

    const timer = setTimeout(async () => {
      abnAlreadyChecked.current = digits
      setAbrBusy(true)
      setAbrNote(null)
      const r = await lookupAbn(digits)
      setAbrBusy(false)
      setAbrResult(r)
      const legal = r.ok ? r.legalName : null
      if (!legal) {
        setAbrNote(r.message)
        return
      }
      /* Fill an empty name; never overwrite one somebody is part-way through typing.
         If they already wrote something, the register's answer goes in the note so
         they can see the difference and decide for themselves. */
      const nameWasBlank = !String(formData.seller_name || '').trim()
      if (nameWasBlank) {
        setFormData((f) => (String(f.seller_name || '').trim() ? f : { ...f, seller_name: legal }))
      }
      setAbrNote(
        (nameWasBlank
          ? 'Seller name filled from the register: ' + legal + '.'
          : 'The register says that ABN is ' + legal + '.') +
          (r.gstRegistered
            ? ' Registered for GST.'
            : ' NOT registered for GST - check the treatment.'),
      )
      setAbrMatches([])
    }, 600)

    return () => clearTimeout(timer)
  }, [formData.seller_abn])

  useEffect(() => {
    /* A private individual has no ABN to find, so do not spend a search looking. */
    const searchable =
      formData.seller_type === 'company' ||
      formData.seller_type === 'dealer' ||
      formData.seller_type === 'auction' ||
      formData.seller_type === 'tender'
    const term = String(formData.seller_name || '').trim()
    const haveAbn = String(formData.seller_abn || '').replace(/\D/g, '').length === 11

    if (!searchable || haveAbn || term.length < 3) return

    const timer = setTimeout(async () => {
      setAbrSearchBusy(true)
      const r = await searchAbnByName(term)
      setAbrSearchBusy(false)
      setAbrMatches(r.ok ? r.results : [])
      if (!r.ok && r.message) setAbrNote(r.message)
    }, 700)

    return () => clearTimeout(timer)
  }, [formData.seller_name, formData.seller_abn, formData.seller_type])

  // Load admin settings on mount
  useEffect(() => {
    loadAdminSettings()
  }, [])

  // Jump straight to the deal form when a vehicle was handed over from search,
  // instead of dropping the user on a blank VIN lookup. (This replaces the
  // alert() that used to tell people to go and re-enter it in Deal Pipeline.)
  useEffect(() => {
    if (!prefill) return
    setFormData((prev) => ({
      ...prev,
      vehicle_make: prefill.make || prev.vehicle_make,
      vehicle_model: prefill.model || prev.vehicle_model,
      vehicle_year: prefill.year || prev.vehicle_year,
      vehicle_vin: prefill.vin || prev.vehicle_vin,
      vehicle_odometer: prefill.odometer_km ? String(prefill.odometer_km) : prev.vehicle_odometer,
      seller_name: prefill.seller_name || prev.seller_name,
      valuation_amount: prefill.price != null ? (prefill.price as any) : prev.valuation_amount,
    }))
    setStep('form')
    setSourcingListingId(prefill.sourcing_listing_id || null)
  }, [prefill])

  const loadAdminSettings = async () => {
    try {
      const { data } = await supabase
        .from('admin_settings')
        .select('margin_threshold_percent, margin_threshold_dollars')
        .single()

      if (data) {
        setAdminSettings(data)
      }
    } catch (err) {
      console.error('Error loading admin settings:', err)
    }
  }

  const handleVehicleFound = async (vehicle: VehicleData) => {
    setVehicleData(vehicle)
    
    // Populate form with vehicle data
    setFormData((prev) => ({
      ...prev,
      vehicle_make: vehicle.make || '',
      vehicle_model: vehicle.model || '',
      vehicle_year: vehicle.year || new Date().getFullYear(),
      vehicle_vin: vehicle.vin || '',
      vehicle_color: vehicle.color || '',
      vehicle_odometer: vehicle.odometer ? String(vehicle.odometer) : '',
      // Winnie's review: the market value should propose the buying price
      // rather than leave the buyer to work it back from the guide. It is a
      // default, not a lock — typing over it is expected, and the panel by the
      // price field says what the override costs.
      valuation_amount: (() => {
        const suggested = suggestedBuyPrice(vehicle.marketValue)
        return suggested === null ? '' : String(suggested)
      })(),
    }))

    // Auto-trigger PPSR check. PPSR requires a registration + state (a VIN
    // alone is not enough for the Autograb certificate endpoint), so this only
    // fires for rego-based lookups.
    if (vehicle.registrationNumber && vehicle.state) {
      triggerPPSRCheck(vehicle.registrationNumber, vehicle.state, vehicle.vin)
    }

    // Fetch vehicle photos (non-blocking; no-ops until the Autograb images
    // product is enabled on the account).
    setPhotos(null)
    if (vehicle.autograbVehicleId) {
      getVehiclePhotos(vehicle.autograbVehicleId, vehicle.color)
        .then((p) => setPhotos(p))
        .catch(() => setPhotos(null))
    }

    // Ask who we are buying from before the form. It sets the document set,
    // the GST treatment and the PPSR grantor, so it should not be a field the
    // buyer scrolls past on their way to the price.
    setStep('sellerType')
  }

  const triggerPPSRCheck = async (rego: string, state: AustralianState, vin: string) => {
    try {
      setPpsr_loading(true)
      const ppsr_result = await checkPPSR(rego, state, vin)
      setPPSR(ppsr_result)
    } catch (err) {
      console.error('PPSR check error:', err)
      // Set warning status but continue
      setPPSR({ vin, status: 'error', encumbered: false })
    } finally {
      setPpsr_loading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    const updatedFormData = {
      ...formData,
      [name]: name === 'vehicle_year' || name === 'vehicle_odometer' || name === 'valuation_amount'
        ? value === '' ? '' : (name === 'valuation_amount' ? parseFloat(value) : parseInt(value))
        : value,
    }
    setFormData(updatedFormData)

    // Recalculate margin if purchase price changes
    if (name === 'valuation_amount' && vehicleData?.marketValue && adminSettings) {
      const purchasePrice = parseFloat(value) || 0
      const newMargin = calculateMargin(purchasePrice, vehicleData.marketValue)
      setMargin(newMargin)
    }
  }


  /*
   * Type 3 sellers - LMCT, auction, tender - always arrive with an invoice that
   * already carries every detail somebody is about to retype. Read it instead.
   *
   * What comes back fills empty fields only. A value already typed is never replaced
   * by an extraction: the person creating the deal is the author, this is an
   * assistant, and the note below says exactly which fields it touched.
   */
  const readInvoice = async (file: File) => {
    setInvoiceBusy(true)
    setInvoiceNote(null)
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('That file could not be opened.'))
        reader.readAsDataURL(file)
      })

      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      const res = await fetch('/api/deals/extract-invoice', {
        method: 'POST',
        headers: token
          ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
          : { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileBase64: dataUrl, contentType: file.type, filename: file.name }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body || !body.success) {
        setInvoiceNote((body && body.error) || 'The invoice could not be read. Enter the details by hand.')
        return
      }

        /* Hold on to the file itself. Reading it only filled the form; the deal it
           belongs to does not exist yet. */
        setInvoiceFile({ dataUrl, filename: file.name, contentType: file.type })
      const f = body.fields || {}
      const filled: string[] = []
      const patch: {
        seller_name?: string
        seller_location?: string
        seller_email?: string
        seller_phone?: string
        seller_abn?: string
        valuation_amount?: string
        vehicle_vin?: string
        vehicle_odometer?: string
        vehicle_rego?: string
        vehicle_rego_state?: string
        vehicle_engine_number?: string
        seller_lmct?: string
        purchase_reference?: string
      } = {}
      const blank = (v: string) => !String(v || '').trim()

      if (f.name && blank(formData.seller_name)) { patch.seller_name = String(f.name); filled.push('name') }
      if (f.address && blank(formData.seller_location)) { patch.seller_location = String(f.address); filled.push('address') }
      if (f.email && blank(formData.seller_email)) { patch.seller_email = String(f.email); filled.push('email') }
      if (f.mobile && blank(formData.seller_phone)) { patch.seller_phone = String(f.mobile); filled.push('mobile') }
      if (f.abn && blank(formData.seller_abn)) { patch.seller_abn = String(f.abn); filled.push('ABN') }
      /* Winnie, 10 Sep: "Purchase Price should pick up from the upload invoice Total
         payable price including GST." The invoice is the record of what was actually
         paid; whatever is already in the box came from Autograb's guide price or an
         earlier estimate, so here the invoice wins rather than being ignored. A figure
         that gets replaced is named in the note, so it never changes silently. */
      let priceReplaced = ''
      if (f.totalInclGst) {
        const wasPrice = String(formData.valuation_amount || '').trim()
        patch.valuation_amount = String(f.totalInclGst)
        filled.push('purchase price')
        if (wasPrice && Number(wasPrice) !== Number(f.totalInclGst)) {
          priceReplaced = ' The purchase price was ' + wasPrice + ' and the invoice total is ' + f.totalInclGst + '; the invoice figure has been used.'
        }
      }
        /* Winnie, 10 Sep: the rest of what is already printed on the invoice. Same rule
           as the fields above - an empty box only, never over something already typed. */
        if (f.invoiceNumber && blank(formData.purchase_reference)) { patch.purchase_reference = String(f.invoiceNumber); filled.push('reference') }
        if (f.lmct && blank(formData.seller_lmct)) { patch.seller_lmct = String(f.lmct); filled.push('LMCT') }
        if (f.rego && blank(formData.vehicle_rego)) { patch.vehicle_rego = String(f.rego).toUpperCase(); filled.push('rego') }
        if (f.odometer && blank(String(formData.vehicle_odometer))) { patch.vehicle_odometer = String(f.odometer); filled.push('odometer') }
        if (f.vin && blank(formData.vehicle_vin)) { patch.vehicle_vin = String(f.vin).toUpperCase(); filled.push('VIN') }
        if (f.engineNumber && blank(formData.vehicle_engine_number)) { patch.vehicle_engine_number = String(f.engineNumber).toUpperCase(); filled.push('engine number') }

      if (filled.length) setFormData((prev) => ({ ...prev, ...patch }))

      /*
       * The invoice gives us an ABN. The register tells us who that ABN actually is.
       *
       * Winnie's Type 3 note asks for the legal entity behind the trading name, and this
       * is the one moment we have the ABN in hand and the person still watching. If
       * ABR_GUID is not set the lookup answers politely and the form carries on with
       * what the invoice said - an unconfigured register must not block a deal.
       */
      let registerNote = ''
      const abnToCheck = patch.seller_abn || formData.seller_abn
      if (abnToCheck && isValidAbn(abnToCheck)) {
        const abr = await lookupAbn(abnToCheck)
        const legal = abr.legalName
        if (abr.ok && legal) {
          /* The register outranks the invoice for who the seller is, but it still does
             not outrank a person: a name already typed is left alone. */
          if (patch.seller_name || blank(formData.seller_name)) {
            setFormData((prev) => ({ ...prev, seller_name: legal }))
            registerNote =
              ' The register says that ABN belongs to ' +
              legal +
              ', so the seller name now shows the legal entity rather than the trading name on the invoice.'
          } else {
            registerNote = ' The register says that ABN belongs to ' + legal + '.'
          }
          registerNote += abr.gstRegistered
            ? ' It is registered for GST.'
            : ' It is NOT registered for GST - check the GST treatment before saving.'
        } else if (abr.message) {
          registerNote = ' The ABN was not confirmed against the register: ' + abr.message
        }
      }

      /* Say what was filled and what was not. An extraction that quietly finds three
         fields out of six is the one that ends up trusted for all six. */
      setInvoiceNote(
        (filled.length
          ? 'Filled from the invoice: ' +
            filled.join(', ') +
            '. Check each against the document before saving.'
          : 'Nothing new was filled. Either the invoice did not carry those details, or those fields already had values - nothing was overwritten.') +
          registerNote + priceReplaced,
      )
    } catch (err) {
      setInvoiceNote(err instanceof Error ? err.message : 'The invoice could not be read.')
    } finally {
      setInvoiceBusy(false)
    }
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (!formData.vehicle_make || !formData.vehicle_model || !formData.seller_name || !formData.valuation_amount) {
        throw new Error('Please fill in all required fields')
      }

      // Winnie's review: seller mobile and email are compulsory. Everything the
      // platform sends the seller — the Vehicle Purchase Agreement link, the DD request, the
      // inspection notice — depends on having both, and a deal created without
      // them silently stalls at the first automated step.
      const sellerEmail = (formData.seller_email || '').trim()
      const sellerPhone = (formData.seller_phone || '').trim()

      if (!sellerEmail) {
        throw new Error("A seller email is required — the Vehicle Purchase Agreement and DD request are sent to it.")
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sellerEmail)) {
        throw new Error('That seller email does not look valid.')
      }
      if (!sellerPhone) {
        throw new Error('A seller mobile is required — the Vehicle Purchase Agreement link is sent by SMS.')
      }
      // Checkpoint (a): Sales must state the payout position before the deal exists.
      // Not a default and not a blank - the seller is asked the same question at
      // signing and the two answers are compared, which only works if both are real.
      if (payoutApplicable !== true && payoutApplicable !== false) {
        throw new Error(
          'Select whether there is finance owing on this vehicle. The seller is asked the same question when they sign, and the two answers are compared.',
        )
      }
      // An override is allowed. An unexplained one is not — it is the only
      // record of why we went over, and nine deals in ten have gone over.
      if (overrideNeeded && overrideReason.length < 10) {
        throw new Error(
          'Paying above the suggested ' +
            money0(suggestedPrice as number) +
            ' needs a reason — a few words on why this car is worth it.',
        )
      }

      if (!/^(?:\+?61|0)4\d{8}$/.test(sellerPhone.replace(/[^\d+]/g, ''))) {
        throw new Error('Use an Australian mobile for the seller, e.g. 0412 345 678.')
      }

      /*
       * A typed ABN is checked against the checksum, not just its length: a
       * transposed digit passes an eleven-digit test and then fails at the
       * register, usually on the day the tax invoice is raised. Blank is still
       * allowed, so a seller whose ABN is not to hand does not block the deal.
       */
      const sellerAbn = (formData.seller_abn || '').replace(/\s/g, '')
      if (sellerAbn && !isValidAbn(sellerAbn)) {
        throw new Error('That seller ABN does not pass the checksum - check the digits.')
      }

      // Calculate margin one more time before submitting
      const purchasePrice = parseFloat(String(formData.valuation_amount)) || 0
      const estimatedSalePrice = vehicleData?.marketValue || 0

      // Only compute a margin when there is a market value to compute it against.
      // Manually-entered vehicles have no Autograb estimate; computing anyway
      // produced grossMargin = -purchasePrice and a permanent -100% on the deal,
      // which then poisoned the margin sort and the Today profit tiles.
      const hasEstimate = estimatedSalePrice > 0
      const finalMargin = hasEstimate ? calculateMargin(purchasePrice, estimatedSalePrice) : null

      const marginValidation = adminSettings && finalMargin
        ? validateMargin(finalMargin, adminSettings.margin_threshold_percent, adminSettings.margin_threshold_dollars)
        : { valid: true }

      // Hard stop on a stolen or written-off vehicle unless explicitly acknowledged.
      if ((ppsr?.status === 'stolen' || ppsr?.status === 'written_off') && !ppsrAcknowledged) {
        throw new Error(
          `PPSR returned "${ppsr.status.replace('_', ' ')}" for this vehicle. Tick the acknowledgement below if you intend to proceed anyway.`
        )
      }

      /* What we can search PPSR with. Autograb hands back the plate and the state it
         was looked up in; the VIN comes from Autograb or from whatever was typed. PPSR
         searches on a VIN and resolves a plate to one first, so either identifier is
         enough. Neither means this car cannot be searched at all - and the deal should
         say that plainly rather than sit there looking merely unchecked. */
      const ppsrVin = (formData.vehicle_vin || vehicleData?.vin || '').trim().toUpperCase()
      const ppsrRego = (formData.vehicle_rego || vehicleData?.registrationNumber || '').trim().toUpperCase()
      const ppsrState = (formData.vehicle_rego_state || vehicleData?.state || '').trim().toUpperCase()
      const canRunPpsr = Boolean(ppsrVin || (ppsrRego && ppsrState))

      /* Winnie, 23 Sep: "when you create a deal from the Autograb sourcing the selected
         vehicle should be removed from the list".

         Every deal is created on the Autograb Sourcing screen, and a deal whose car was
         looked up carries autograb_vehicle_id - which is exactly what puts a row on that
         screen. So the new deal landed back on the list it had just been created from, and
         a car at Under Offer or Contract Signed sat there looking like it was still up for
         grabs. The 11 Sep stamp below only covered the "+ Deal" button on an existing row,
         never the header "+ Create Deal", which is how today's deals were made.

         The deal is born stamped as converted - into itself - using the same three columns
         and the same screen rules as a listing turned into a deal. It is not deleted from
         anywhere: "Show converted" still lists it, and "Return to sourcing list" still puts
         it back. The id is minted here so the stamp goes in with the insert; stamping in a
         second write let the realtime channel paint the row for a moment first. */
      const newDealId = crypto.randomUUID()
      const createdAtIso = new Date().toISOString()

      const deal_data = {
        id: newDealId,
        sourcing_converted_deal_id: newDealId,
        sourcing_converted_at: createdAtIso,
        sourcing_converted_by: userId,
        user_id: userId,
        vehicle_make: tidyName(formData.vehicle_make),
        vehicle_model: tidyName(formData.vehicle_model),
        vehicle_year: formData.vehicle_year,
        vehicle_vin: formData.vehicle_vin,
        vehicle_color: formData.vehicle_color,
        vehicle_odometer: formData.vehicle_odometer ? parseInt(String(formData.vehicle_odometer)) : null,
      engine_number: formData.vehicle_engine_number ? String(formData.vehicle_engine_number).toUpperCase().trim() : null,
      seller_lmct: formData.seller_lmct || null,
      /* The seller's own invoice number. Winnie asked for it as the deal's Reference, so
         a payment or a query can be matched back to the document it came from. */
      purchase_reference: formData.purchase_reference || null,
        seller_name: formData.seller_name,
        seller_email: formData.seller_email,
        seller_phone: formData.seller_phone,
        seller_location: formData.seller_location,
        /*
         * Stored without spaces so a register value and a hand-typed one match.
         * Null rather than an empty string, because the GST register reads a
         * present-but-blank ABN as a seller who was asked and had none.
         */
        seller_abn: (formData.seller_abn || '').replace(/\s/g, '') || null,
        valuation_amount: purchasePrice,
        // Every deal enters the pipeline at "Sourced". The status is advanced only
        // by transitionDealStatus, which evaluates the preconditions; letting the
        // creator pick "Paid" here bypassed the Vehicle Purchase Agreement, DD, inspection and
        // the entire two-approver payment chain.
        deal_status: 'sourced',
        seller_type: formData.seller_type,
        finance_payout_applicable: payoutApplicable,
        /*
         * The seller type decides the GST treatment, so the treatment is recorded
         * here rather than left to the column default. That default stamped
         * notional_itc on every new deal - including the company, dealer, auction
         * and tender buys that come with a tax invoice - and it beat treatmentOf's
         * inference, because that only fires when the stored value is null. Every
         * tax-invoice purchase created through this form was landing in the book as
         * a margin-scheme one until somebody edited the deal by hand.
         *
         * Recorded as 'inferred': it came from the seller type, not from a person
         * choosing it and not from the register.
         */
        gst_treatment: treatmentOf({ gst_treatment: null, seller_type: formData.seller_type }),
        gst_treatment_source: 'inferred',
        notes: formData.notes,
        
        // Autograb fields. These are only real when Autograb actually
        // identified the car. The manual-entry path in VehicleLookup builds a
        // VehicleData with make: '' and year: new Date().getFullYear(), because
        // the type makes year a required number - and that invented year used to
        // land in the autograb year column. Staff views prefer it over the year
        // the operator typed, so a 2019 car entered by hand read as 2026 in the
        // deal header, the pipeline card and the Vehicle Purchase Agreement screen, while the
        // seller portal and the signed contract correctly read 2019.
        //
        // vehicleData.make is the honest test: it is empty on the manual path.
        autograb_make: vehicleData?.make || null,
        autograb_model: vehicleData?.make ? vehicleData.model || null : null,
        autograb_year: vehicleData?.make ? vehicleData.year ?? null : null,
        autograb_odometer: vehicleData?.make ? vehicleData.odometer ?? null : null,
        autograb_color: vehicleData?.make ? vehicleData.color || null : null,
        autograb_market_value: vehicleData?.make ? vehicleData.marketValue ?? null : null,
        autograb_fetched_at: vehicleData?.make ? new Date().toISOString() : null,

      // Winnie's ruling, 24 August: paying over the suggestion is allowed but
      // has to be explained and tracked. The suggestion is stored as well as
      // the reason — the market value moves, so recomputing it later would not
      // show what the buyer was looking at.
      purchase_price_suggested: suggestedBuyPrice(vehicleData?.marketValue),
      purchase_override_reason: overrideNeeded ? overrideReason : null,
      purchase_override_at: overrideNeeded ? new Date().toISOString() : null,
      purchase_override_by: overrideNeeded ? userId : null,
        
        // Margin fields
        estimated_sale_price: hasEstimate ? estimatedSalePrice : null,
        gross_margin: finalMargin ? finalMargin.grossMargin : null,
        margin_percent: finalMargin ? finalMargin.marginPercent : null,
        margin_warning: !marginValidation.valid,
        margin_warning_reason: marginValidation.warning || null,
        
        // PPSR fields. Autograb's PPSR certificate returns boolean flags (not a
        // finance amount), so ppsr_finance_amount stays null; details holds the
        // full certificate payload including the PDF URL.
        /* The real search has not run yet - it is fired the moment the deal exists,
           further down. 'pending' is what the sourcing screen reads to say Checking.
           'unavailable' is a car we hold neither a VIN nor a rego for, which is a
           different thing from one nobody has got around to checking. */
        ppsr_status: canRunPpsr ? 'pending' : 'unavailable',
        ppsr_check_date: null,
        ppsr_finance_amount: null,
        ppsr_encumbrancee: null,
        ppsr_details: ppsr?.details || null,
        /* Keep the plate. The form used it to look the car up and then threw it away,
           which is why not one deal in the book carries a rego, and why a rego-based
           PPSR search was impossible before today. */
        registration_number: (formData.vehicle_rego ? String(formData.vehicle_rego).toUpperCase().trim() : '') || ppsrRego || null,
        registration_state: ppsrState || null,

        // Phase 3b: photos (populated once Autograb images product is enabled)
        autograb_vehicle_id: vehicleData?.autograbVehicleId || null,
        photo_url: photos?.primary || null,
        photo_source: photos?.source || null,
        additional_photos: photos?.additional && photos.additional.length > 0 ? photos.additional : null,
        photo_fetched_at: photos ? new Date().toISOString() : null,

        negotiation_started_at: new Date().toISOString()
      }

      const { data: created, error: insertError } = await supabase
        .from('deals')
        .insert(deal_data)
        .select('id')
        .single()

      if (insertError) throw insertError

      toast.saved()

      /*
       * The seller joins the Contacts registry (Winnie, 15 Sep).
       *
       * Type 3/4/5 - the licensed dealers, auctions and tender houses - are recorded as both
       * Seller and Dealer, because that is what they are and the buyer-side lookup should find
       * them next time without anybody re-typing the ABN. A private individual (Type 1) is
       * recorded as a plain Seller with whatever contact details the form collected.
       *
       * Deliberately not awaited into the failure path: `matchOrCreate` swallows its own
       * errors and returns null, so a registry that is unreachable can never stop a deal being
       * created. The deal is the thing that matters here.
       */
      {
        const sellerTypes: ContactType[] =
          formData.seller_type === 'dealer' || formData.seller_type === 'auction' || formData.seller_type === 'tender'
            ? ['Seller', 'Dealer']
            : ['Seller']
        void contactsService.matchOrCreate({
          name: String(formData.seller_name || '').trim(),
          abn: formData.seller_abn || null,
          email: formData.seller_email || null,
          phone: formData.seller_phone || null,
          address: formData.seller_location || null,
          contact_type: sellerTypes,
        })
      }

      /* Fire the PPSR search now the deal exists. Deliberately not awaited: it writes
         its answer onto the deal itself, so nobody stands watching a spinner, and PPSR
         being slow or down cannot stop a car being recorded. The row reads Checking
         until the answer lands. */
      if (created?.id && canRunPpsr) {
        void ppsrService.runSearch(created.id, {
          vin: ppsrVin,
          rego: ppsrRego,
          state: ppsrState,
        })
      }

        /* Winnie, 11 Sep: a listing that has produced a deal drops off the AutoGrab sourcing
           list, so the same car cannot be turned into a second deal. The listing row is kept -
           it is the trail back to the feed entry, and the deal may yet be cancelled - so it is
           only stamped. The null guard means two people pressing "+ Deal" on the same listing
           cannot overwrite each other: the second update matches no row and the first stands. */
        if (created?.id && sourcingListingId) {
          const { error: stampError } = await supabase
            .from('deals')
            .update({
              sourcing_converted_deal_id: created.id,
              sourcing_converted_at: new Date().toISOString(),
              sourcing_converted_by: userId,
            })
            .eq('id', sourcingListingId)
            .is('sourcing_converted_deal_id', null)
          if (stampError) {
            // The deal is created and that is what matters; the listing can be removed by hand.
            console.error('Could not mark the sourcing listing as used', stampError)
          }
        }


        /* Winnie, 11 Sep: a licensed seller's invoice is the whole paper trail, so a
           Type 3/4/5 deal is filed with it and reaches Committed straight away.

           The invoice goes on the deal first and the status machinery is then asked to
           advance: both preconditions run, both write their own line in the status log,
           and nothing here stamps a status by hand. If the filing fails the deal still
           exists at Sourced with everything typed into it — which is recoverable — so
           the note says what happened rather than throwing the whole submission away. */
        const st = String(formData.seller_type || '').toLowerCase()
        const issuesOwnInvoice = st === 'dealer' || st === 'auction' || st === 'tender'
        if (created?.id && invoiceFile && issuesOwnInvoice) {
          try {
            const { data: sess } = await supabase.auth.getSession()
            const tok = sess.session?.access_token
            const filed = await fetch('/api/deals/tax-invoice', {
              method: 'POST',
              headers: tok
                ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok }
                : { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                dealId: created.id,
                dataBase64: invoiceFile.dataUrl,
                filename: invoiceFile.filename,
                contentType: invoiceFile.contentType,
              }),
            })
            const filedBody = await filed.json().catch(() => null)
            if (!filed.ok || !filedBody || !filedBody.success) {
              setInvoiceNote(
                (filedBody && filedBody.error) ||
                  'The deal was created, but the invoice could not be filed against it. Upload it from the workspace to move the deal on.'
              )
            } else {
              await autoAdvanceDealIfReady(created.id)
            }
          } catch (err) {
            setInvoiceNote(
              'The deal was created, but the invoice could not be filed against it. Upload it from the workspace to move the deal on.'
            )
          }
        }


      // Reset form
        setInvoiceFile(null)
      setFormData({
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: new Date().getFullYear(),
        vehicle_vin: '',
        vehicle_color: '',
        vehicle_odometer: '',
        vehicle_rego: '',
        vehicle_rego_state: '',
        vehicle_engine_number: '',
        seller_lmct: '',
        purchase_reference: '',
        seller_name: '',
        seller_email: '',
        seller_phone: '',
        seller_location: '',
        seller_abn: '',
        valuation_amount: '',
        seller_type: 'individual',
        purchase_override_reason: '',
        notes: '',
      })
      setPpsrAcknowledged(false)
      setSourcingListingId(null)
      setAbrNote(null)
      setAbrMatches([])
      setAbrSearchTerm('')
      setVehicleData(null)
      setMargin(null)
      setPPSR(null)
      setPhotos(null)
      setStep('lookup')

      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create deal')
    } finally {
      setLoading(false)
    }
  }

  // Step 1: Vehicle Lookup
  if (step === 'lookup') {
    return (
      <div className="create-deal-form">
        <h3>Step 1: Find Vehicle</h3>
        <VehicleLookup user={user} onVehicleFound={handleVehicleFound} />
      </div>
    )
  }

  // The suggestion, and how far the typed price sits above it. Both are null
  // when we have no market value to work from, and null must render as nothing
  // rather than as a confident zero.
  /* Make and model are the key the buying brief groups a model's history under. Left
     as free text, one person typing "Prado" where Autograb said "Landcruiser Prado"
     quietly splits a model in two, and each half is then priced off too little
     history to mean anything. So once Autograb has named the car, only an admin can
     rename it. A car Autograb could not name stays editable, or it could not be
     entered at all. */
  const vehicleNamedByAutograb = Boolean(vehicleData?.make && vehicleData?.model)
  const canRenameVehicle = user.role === 'admin' || !vehicleNamedByAutograb

  const suggestedPrice = suggestedBuyPrice(vehicleData?.marketValue)
  const typedPrice =
    formData.valuation_amount === '' ? null : Number(formData.valuation_amount)
  const overBy = overSuggestion(typedPrice, vehicleData?.marketValue)
  const overrideReason = formData.purchase_override_reason.trim()
  const overrideNeeded =
    suggestedPrice !== null && typedPrice !== null && typedPrice > suggestedPrice

  if (step === 'sellerType') {
    return (
      <div className="seller-type-step">
        <h2>Who are we buying from?</h2>
        <p className="seller-type-lead">
          This sets the due-diligence documents, the GST treatment and the PPSR
          grantor. It can be changed on the next screen.
        </p>

        <div className="seller-type-vehicle">
          <span className="stv-name">
            {vehicleData?.year} {vehicleData?.make} {vehicleData?.model}
          </span>
          {suggestedPrice !== null && (
            <span className="stv-price">
              Market value {money0(Number(vehicleData?.marketValue))} · suggested{' '}
              {money0(suggestedPrice)}
            </span>
          )}
          {suggestedPrice === null && (
            <span className="stv-price stv-price-missing">
              No market value from Autograb &mdash; no suggested price for this one.
            </span>
          )}
        </div>

        <div className="seller-type-grid">
          {SELLER_TYPES.map((option, index) => (
            <button
              key={option.value}
              type="button"
              className="seller-type-card"
              onClick={() => {
                setFormData((prev) => ({ ...prev, seller_type: option.value }))
                setStep('form')
              }}
            >
              <span className="stc-index">Type {index + 1}</span>
              <span className="stc-label">{option.label}</span>
              <span className="stc-note">{option.note}</span>
            </button>
          ))}
        </div>

        <button type="button" className="seller-type-back" onClick={() => setStep('lookup')}>
          ← Choose a different vehicle
        </button>
      </div>
    )
  }

  // Step 2: Deal Form
  return (
    <form onSubmit={handleSubmit} className="create-deal-form">
      <div className="form-header">
        <h3>Step 2: Create Deal</h3>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setStep('lookup')
            setVehicleData(null)
            setMargin(null)
            setPPSR(null)
            setPhotos(null)
          }}
        >
          ← Change Vehicle
        </button>
      </div>

      {/* Vehicle Summary */}
      {vehicleData && (
        <div className="vehicle-summary">
          <div className="summary-item">
            <span className="label">Vehicle</span>
            <span className="value">
              {vehicleData.year} {vehicleData.make} {vehicleData.model}
            </span>
          </div>
          {vehicleData.marketValue && (
            <div className="summary-item">
              <span className="label">Market Value</span>
              <span className="value highlight">${vehicleData.marketValue.toLocaleString()}</span>
            </div>
          )}
          {ppsr && (
            <div className="summary-item">
              <span className="label">PPSR Status</span>
              <span className={`value status-${ppsr.status}`}>
                {ppsr.status === 'clear'
                  ? '✅ Clear'
                  : ppsr.status === 'encumbered'
                  ? '⚠️ Encumbered (finance owing)'
                  : ppsr.status === 'stolen'
                  ? '🚨 Stolen'
                  : ppsr.status === 'written_off'
                  ? '🚨 Written off'
                  : '⚠️ Error'}
                {ppsr.certificateUrl && (
                  <>
                    {' '}
                    <a href={ppsr.certificateUrl} target="_blank" rel="noopener noreferrer">
                      certificate
                    </a>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Vehicle Details Section (editable — required for manual entry, and lets
          users correct Autograb-sourced values) */}
      <div className="form-section">
        <h4>Vehicle Details</h4>

        <div className="form-row">
          <div className="form-group">
            <label>Make *</label>
            <input
              type="text"
              name="vehicle_make"
              value={formData.vehicle_make}
              onChange={handleChange}
              placeholder="e.g., Toyota"
              readOnly={!canRenameVehicle}
              required
            />
          </div>
          <div className="form-group">
            <label>Model *</label>
            <input
              type="text"
              name="vehicle_model"
              value={formData.vehicle_model}
              onChange={handleChange}
              placeholder="e.g., Camry"
              readOnly={!canRenameVehicle}
              required
            />
          </div>
        </div>

        {!canRenameVehicle && (
          <p className="vehicle-locked-note">
            Make and model are Autograb's, and locked, so the buying brief keeps one
            card per model instead of splitting it across spellings. An admin can
            correct them if Autograb has the car wrong.
          </p>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Year</label>
            <input
              type="number"
              name="vehicle_year"
              value={formData.vehicle_year}
              onChange={handleChange}
              placeholder="2024"
            />
          </div>
          <div className="form-group">
            <label>Odometer (km)</label>
            <input
              type="number"
              name="vehicle_odometer"
              value={formData.vehicle_odometer}
              onChange={handleChange}
              placeholder="e.g., 45000"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>VIN</label>
            <input
              type="text"
              name="vehicle_vin"
              value={formData.vehicle_vin}
              onChange={handleChange}
              placeholder="17-character VIN"
            />
          </div>
          <div className="form-group">
            <label>Rego</label>
            <input
              type="text"
              name="vehicle_rego"
              value={formData.vehicle_rego}
              onChange={handleChange}
              placeholder="e.g., DTA46R"
            />
          </div>
          <div className="form-group">
            <label>Registration State</label>
            <select
              name="vehicle_rego_state"
              value={formData.vehicle_rego_state || (vehicleData?.state || '').toUpperCase()}
              onChange={handleChange}
            >
              <option value="">Select…</option>
              <option value="NSW">NSW</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="SA">SA</option>
              <option value="WA">WA</option>
              <option value="TAS">TAS</option>
              <option value="NT">NT</option>
              <option value="ACT">ACT</option>
            </select>
            <small>PPSR searches a plate against the state it is registered in. A VIN on its own needs no state.</small>
          </div>
          <div className="form-group">
            <label>Engine Number</label>
            <input
              type="text"
              name="vehicle_engine_number"
              value={formData.vehicle_engine_number}
              onChange={handleChange}
              placeholder="As printed on the invoice"
            />
          </div>
          <div className="form-group">
            <label>Color</label>
            <input
              type="text"
              name="vehicle_color"
              value={formData.vehicle_color}
              onChange={handleChange}
              placeholder="e.g., White"
            />
          </div>
        </div>
      </div>

      {/* Seller Information Section */}
      <div className="form-section">
        <h4>Seller Information</h4>

          {(formData.seller_type === 'dealer' ||
            formData.seller_type === 'auction' ||
            formData.seller_type === 'tender') && (
            <div className="form-row">
              <div className="form-group">
                <label>Seller Invoice (Type 3)</label>
                <FileDropZone
                  accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                  label="Upload Invoice"
                  busy={invoiceBusy}
                  busyLabel="Reading the invoice…"
                  onFiles={(files) => readInvoice(files[0])}
                />
                <small className="field-hint">
                  {invoiceBusy
                    ? 'Reading the invoice...'
                    : 'Upload the dealer, auction or tender invoice and the seller name, address, email, mobile, ABN and total incl GST are filled in below. The purchase price is always taken from the invoice total; every other box is filled only if it is empty.'}
                </small>
                {invoiceNote && <small className="field-hint">{invoiceNote}</small>}
              </div>
            </div>
          )}
        <div className="form-row">
          <div className="form-group">
            <label>LMCT</label>
            <input
              type="text"
              name="seller_lmct"
              value={formData.seller_lmct}
              onChange={handleChange}
              placeholder="Dealer licence number"
            />
          </div>
          <div className="form-group">
            <label>Reference</label>
            <input
              type="text"
              name="purchase_reference"
              value={formData.purchase_reference}
              onChange={handleChange}
              placeholder="The seller’s invoice number"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Seller Name *</label>
            <input
              type="text"
              name="seller_name"
              value={formData.seller_name}
              onChange={handleChange}
              placeholder="John Doe"
              required
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <LocationAutocomplete
              id="seller-location"
              name="seller_location"
              value={formData.seller_location}
              placeholder="Start typing a street address…"
              onChange={(v) => setFormData((f) => ({ ...f, seller_location: v }))}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="seller_email"
              value={formData.seller_email}
              onChange={handleChange}
              placeholder="seller@example.com"
              required
            />
            <small className="field-hint">
              The Vehicle Purchase Agreement and due-diligence request are both sent here.
            </small>
          </div>
          <div className="form-group">
            <label>Mobile *</label>
            <input
              type="tel"
              name="seller_phone"
              value={formData.seller_phone}
              onChange={handleChange}
              placeholder="0412 345 678"
              required
            />
            <small className="field-hint">
              Used for the Vehicle Purchase Agreement link and inspection SMS.
            </small>
          </div>
        </div>
        {formData.seller_type !== 'individual' && (
          <div className="form-row">
            <div className="form-group">
              <label>Seller ABN</label>
              <div className="abr-row">
                <input
                  type="text"
                  name="seller_abn"
                  value={formData.seller_abn}
                  onChange={handleChange}
                  placeholder="11 222 333 444"
                  aria-label="Seller ABN"
                />
                <button
                  type="button"
                  className="abr-btn"
                  disabled={!formData.seller_abn || abrBusy}
                  onClick={async () => {
                    setAbrNote(null)
                    setAbrBusy(true)
                    const r = await lookupAbn(formData.seller_abn)
                    setAbrBusy(false)
                    setAbrResult(r)
                    setAbrNote(r.message)
                    /*
                     * The register is the authority on the legal entity name, and that
                     * is the name the purchase agreement is drawn in. Filling it here
                     * is the point of the check; leaving it to be retyped is how the
                     * name on a contract ends up not matching the name on the ABN.
                     */
                    if (r.ok && r.legalName) {
                      const legalName = r.legalName
                      setFormData((f) => ({ ...f, seller_name: legalName }))
                    }
                  }}
                >
                  {abrBusy ? 'Checking...' : 'Check the ABR'}
                </button>
              </div>
              <div className="abr-row">
                <input
                  type="text"
                  value={abrSearchTerm}
                  onChange={(e) => setAbrSearchTerm(e.target.value)}
                  placeholder="or search the register by trading name"
                  aria-label="Search the ABR by business or trading name"
                />
                <button
                  type="button"
                  className="abr-btn"
                  disabled={abrSearchTerm.trim().length < 3 || abrSearchBusy}
                  onClick={async () => {
                    setAbrNote(null)
                    setAbrMatches([])
                    setAbrSearchBusy(true)
                    const r = await searchAbnByName(abrSearchTerm)
                    setAbrSearchBusy(false)
                    setAbrNote(r.message)
                    setAbrMatches(r.results)
                  }}
                >
                  {abrSearchBusy ? 'Searching...' : 'Search the ABR'}
                </button>
              </div>
              {abrMatches.length > 0 && (
                <ul className="abr-matches">
                  {abrMatches.map((m) => (
                    <li key={m.abn}>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((f) => ({ ...f, seller_abn: m.abn }))
                          setAbrMatches([])
                          setAbrNote('ABN filled in from ' + m.name + '. Check the ABR to pull the legal name.')
                        }}
                      >
                        <strong>{m.name}</strong>
                        <span>
                          {m.abn}
                          {m.nameType ? ' - ' + m.nameType : ''}
                          {m.state ? ' - ' + m.state : ''}
                          {m.postcode ? ' ' + m.postcode : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {abrNote && <p className="abr-note">{abrNote}</p>}
              {/* Winnie, 15 Sep: a successful lookup against the register offers to save the
                  result as a Contact, so the registry grows from real usage. */}
              <ContactSavePrompt
                abn={formData.seller_abn}
                result={abrResult}
                type={
                  formData.seller_type === 'dealer' ||
                  formData.seller_type === 'auction' ||
                  formData.seller_type === 'tender'
                    ? 'Dealer'
                    : 'Seller'
                }
                suggest={{
                  name: formData.seller_name,
                  email: formData.seller_email,
                  phone: formData.seller_phone,
                  address: formData.seller_location,
                }}
              />
              <small className="field-hint">
                The ABN goes on the tax invoice and the purchase agreement. Checking it
                against the register fills in the legal entity name.
              </small>
            </div>
          </div>
        )}
      </div>

      {/* Deal Details Section */}
      <div className="form-section">
        <h4>Deal Details</h4>

        <div className="form-group">
          <label>Purchase Price (What we're paying) *</label>
          <div className="price-input-group">
            <span className="currency">$</span>
            <input
              type="number"
              name="valuation_amount"
              value={formData.valuation_amount}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              required
            />
          </div>
          {suggestedPrice !== null && (
            <div className={`suggested-price${overBy !== null && overBy > 0 ? ' suggested-price-over' : ''}`}>
              <div className="suggested-headline">
                <span className="suggested-label">Suggested purchase price</span>
                <strong>{money0(suggestedPrice)}</strong>
                <button
                  type="button"
                  className="suggested-apply"
                  onClick={() => setFormData((prev) => ({ ...prev, valuation_amount: String(suggestedPrice) }))}
                >
                  Use it
                </button>
              </div>
              <p className="suggested-note">
                Market value {money0(Number(vehicleData?.marketValue))} less a{' '}
                {money0(targetMarginDollars(suggestedPrice))} guide target, rounded down to the
                nearest fifty. Type over it if the deal warrants it.
              </p>
              {overrideNeeded && typedPrice !== null && (
                <>
                  <p className="suggested-warning">
                    {money0(overBy as number)} above the suggestion. At {money0(typedPrice)} the
                    margin is {money0(Number(vehicleData?.marketValue) - typedPrice)} against a{' '}
                    {money0(targetMarginDollars(Number(vehicleData?.marketValue)))} target.
                  </p>
                  <label className="suggested-reason">
                    <span>Why are we paying over? <em>Required</em></span>
                    <textarea
                      name="purchase_override_reason"
                      value={formData.purchase_override_reason}
                      onChange={handleChange}
                      rows={2}
                      placeholder="e.g. one owner, full books, buyer already lined up at $74k"
                    />
                    <small>
                      Saved against the deal with your name and the time, so it can be
                      reviewed later.
                    </small>
                  </label>
                </>
              )}
            </div>
          )}
          {suggestedPrice === null && (
            <p className="suggested-missing">
              Autograb returned no market value for this vehicle, so there is no
              suggested price to hold the buy against. Enter the purchase price
              yourself, and say why in the notes.
            </p>
          )}
        </div>

        {/* Margin Display */}
        {margin && adminSettings && (
          <div className={`margin-display ${margin.isLoss ? 'loss' : !validateMargin(margin, adminSettings.margin_threshold_percent, adminSettings.margin_threshold_dollars).valid ? 'warning' : 'success'}`}>
            <div className="margin-row">
              <span>Estimated Margin</span>
              <span>${margin.grossMargin.toLocaleString()}</span>
            </div>
            <div className="margin-row">
              <span>Margin %</span>
              <span>{margin.marginPercent.toFixed(1)}%</span>
            </div>
            {!validateMargin(margin, adminSettings.margin_threshold_percent, adminSettings.margin_threshold_dollars).valid && (
              <div className="margin-warning">
                ⚠️ {validateMargin(margin, adminSettings.margin_threshold_percent, adminSettings.margin_threshold_dollars).warning}
              </div>
            )}
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Seller Type *</label>
            <select name="seller_type" value={formData.seller_type} onChange={handleChange}>
              {SELLER_TYPES.map((option, index) => (
                <option key={option.value} value={option.value}>
                  Type {index + 1} — {option.label}
                </option>
              ))}
            </select>
            <small className="field-hint">
              Sets the due-diligence document set, the GST/ITC treatment and the PPSR grantor.
            </small>
          </div>
          <div className="form-group">
            <label>Status</label>
            <div className="status-locked">
              Sourced
              <small className="field-hint">
                Deals always start at Sourced and advance only when their preconditions are met.
              </small>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Finance Owing on This Vehicle *</label>
            <select
              value={payoutApplicable === null ? '' : payoutApplicable ? 'yes' : 'no'}
              onChange={(e) =>
                setPayoutApplicable(e.target.value === '' ? null : e.target.value === 'yes')
              }
            >
              <option value="">Select an answer</option>
              <option value="yes">Yes — finance owing, a payout letter is required</option>
              <option value="no">No — the vehicle is owned outright</option>
            </select>
            <small className="field-hint">
              The seller answers this again on their signing link. If the two answers disagree the
              deal is held automatically until somebody resolves it.
            </small>
          </div>
        </div>

        {(ppsr?.status === 'stolen' || ppsr?.status === 'written_off') && (
          <div className="ppsr-block">
            <strong>PPSR returned “{ppsr.status.replace('_', ' ')}” for this vehicle.</strong>
            <label className="ppsr-ack">
              <input
                type="checkbox"
                checked={ppsrAcknowledged}
                onChange={(e) => setPpsrAcknowledged(e.target.checked)}
              />
              I have reviewed the PPSR result and intend to proceed anyway.
            </label>
          </div>
        )}

        {!ppsr && (
          <div className="ppsr-note">
            No PPSR search has run for this vehicle — a search only runs automatically on a
            rego lookup. This deal will be created with no PPSR result on file.
          </div>
        )}

        <div className="form-group">
          <label>Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any additional notes..."
            rows={3}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="form-actions">
        <button type="submit" disabled={loading || ppsr_loading} className="primary-button">
          {loading ? 'Creating...' : 'Create Deal'}
        </button>
      </div>
    </form>
  )
}
